export const roundNumberWithPrecision = (
  value: number,
  precision: number = 2,
): number => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: false,
    roundingMode: 'halfExpand',
  } as Intl.NumberFormatOptions & { roundingMode: 'halfExpand' }).format(value);
  return Number(formatted);
};

/**
 * Formats a number into a localized currency string.
 * If negative, places minus sign after the currency symbol (e.g. "$ -1,234.56").
 * Adds a space between symbol and number only if `spaceBetweenSymbolAndNumber` is true.
 * formatCurrency(1234.56);
 * // "$ 1,234.56"
 * formatCurrency(1234.56, { spaceBetweenSymbolAndNumber: false });
 * // "$1,234.56"
 * formatCurrency(-1234.56);
 * // "$ -1,234.56"
 * formatCurrency(1234.56, { currency: 'NPR', locale: 'ne-NP'});
 * // "रु 1,234.56"
 */

interface FormatCurrencyOptions {
  currency?: string;
  locale?: string;
  precision?: number;
  spaceBetweenSymbolAndNumber?: boolean;
}

export function formatCurrencyWithPrecision(
  value: number = 0,
  options?: FormatCurrencyOptions,
): string {
  const {
    currency = 'USD',
    locale = 'en-US',
    precision = 2,
    spaceBetweenSymbolAndNumber = true,
  } = options || {};

  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const rounded = roundNumberWithPrecision(absValue, precision);

  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).formatToParts(rounded);

  const symbol = parts.find((p) => p.type === 'currency')?.value ?? '';
  const number = parts
    .filter((p) => p.type !== 'currency' && p.type !== 'minusSign')
    .map((p) => p.value)
    .join('');

  const space = spaceBetweenSymbolAndNumber ? ' ' : '';
  const sign = isNegative ? '-' : '';

  return `${symbol}${space}${sign}${number}`;
}
